import React, { memo, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
// Use centralized data context APIs for completion so cache invalidation stays consistent across app.
import { useData } from '../../contexts/DataContext';
import './AssignmentCard.css';

const AssignmentCard = memo(({ 
  assignment, 
  formatDateTime,
  formatDate,
  onClick,
  onRefresh
}) => {
  const { toggleAssignmentCompleted } = useData?.() || {};
  const [toggling, setToggling] = useState(false);
  // Local optimistic completed state so user doesn't need to click twice before the parent refreshes.
  const initialCompleted = Boolean(
    assignment.completed ||
    assignment.status === 'Completed' ||
    assignment.submissionStatus === 'Completed'
  );
  const [completedLocal, setCompletedLocal] = useState(initialCompleted);
  const lastAssignmentIdRef = useRef(assignment.assignmentId || assignment.id);
  // Remove server completion gating – rely purely on optimistic local state and revert on error.

  // Only reset local state if the assignment identity truly changes.
  useEffect(() => {
    const currentId = assignment.assignmentId || assignment.id;
    if (currentId !== lastAssignmentIdRef.current) {
      lastAssignmentIdRef.current = currentId;
      const fresh = Boolean(
        assignment.completed ||
        assignment.status === 'Completed' ||
        assignment.submissionStatus === 'Completed'
      );
      setCompletedLocal(fresh);
    }
  }, [assignment.assignmentId, assignment.id, assignment.completed, assignment.status, assignment.submissionStatus]);

  const isCompleted = completedLocal;

  const courseName =
    assignment.courseName ??
    assignment.course?.name ??
    assignment.endeavorName ??
    assignment.endeavor?.name ??
    assignment.endeavorTitle ??
    assignment.endeavor?.title ??
    assignment.course?.title ??
    null;

  const rawEventDate = assignment.eventStartDatetime || assignment.eventDate || null;
  const rawDue = rawEventDate || assignment.dueDatetime || assignment.dueDate;
  const dueText = rawDue
    ? (formatDateTime ? formatDateTime(rawDue) : rawDue)
    : '';

  const id = assignment.assignmentId || assignment.id;

  const isFromSyllabus =
    Boolean(assignment.isFromSyllabus) ||
    /SYLLABUS/i.test(String(assignment.source || ''));

  const weightText = Number.isFinite(Number(assignment.weightPercent))
    ? `${Number(assignment.weightPercent)}%`
    : null;

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (!id || toggling) return;
    // Because React synthetic event may batch, read intended next state from opposite of local
    const nextChecked = !completedLocal; // we control checked prop; trust our local state rather than e.target
    const prevChecked = completedLocal;

    // Optimistic immediate UI update.
    setCompletedLocal(nextChecked);

    try {
      setToggling(true);

      // Check if marking incomplete - ask for confirmation
      if (!nextChecked && prevChecked) {
        const proceed = window.confirm('Mark this assignment as not completed?');
        if (!proceed) {
          setCompletedLocal(true);
          setToggling(false);
          return;
        }
      }

      // Call centralized toggle function with source tracking
      if (typeof toggleAssignmentCompleted === 'function') {
        await toggleAssignmentCompleted(assignment, { source: 'assignment-card' });
      } else {
        console.warn('toggleAssignmentCompleted not available on context');
      }
    } catch (err) {
      console.error('Assignment toggle failed:', err);
      // Revert optimistic state upon error.
      setCompletedLocal(prevChecked);
      alert(err.message || 'Action failed.');
    } finally {
      setToggling(false);
      onRefresh && onRefresh();
    }
  };

  return (
    <div 
      className={`assignment-card compact ${onClick ? 'clickable' : ''} ${isCompleted ? 'completed' : ''}`}
      onClick={onClick ? () => onClick(assignment) : undefined}
      title={assignment.title || assignment.name}
    >
      <div className="assignment-header-title">
        <h4 className="assignment-title single-line">
          {isFromSyllabus && '🤖 '}
          {assignment.title || assignment.name}
          {weightText && ` · ${weightText}`}
        </h4>
        {courseName && (
          <div className="assignment-course single-line" title={courseName}>
            {assignment.groupWork ? '👥 ' : ''}{courseName}
          </div>
        )}
      </div>

      <div className="assignment-row">
        <span className="due-date-text">
          {dueText}
        </span>

        <motion.button
          type="button"
          className={`complete-checkbox-btn ${isCompleted ? 'is-completed' : ''}`}
          title={isCompleted ? 'Completed' : (toggling ? 'Updating...' : 'Mark complete')}
          aria-label={isCompleted ? 'Completed' : 'Mark complete'}
          aria-checked={isCompleted}
          role="checkbox"
          disabled={toggling}
          whileTap={{ scale: 1.2 }}
          onClick={(e) => { e.stopPropagation(); handleToggle(e); }}
          style={isCompleted ? {
            background: 'linear-gradient(135deg,#3b82f6 0%, #60a5fa 55%, #93c5fd 100%)',
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 2px rgba(59,130,246,.35), 0 2px 4px rgba(0,0,0,.15)'
          } : undefined}
        >
          <motion.span
            className="check-icon"
            initial={false}
            animate={isCompleted ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >✓</motion.span>
        </motion.button>
      </div>
    </div>
  );
});

AssignmentCard.displayName = 'AssignmentCard';

export default AssignmentCard;
